import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Mail, Smartphone } from "lucide-react";

export default function Auth({ role = "booker" }) {
  const loc = useLocation();
  const isPartner = role === "restaurant" || loc.pathname.startsWith("/partner");
  const isAdmin = role === "admin" || loc.pathname.startsWith("/admin/login");
  // Partners log in by email; bookers & admins log in by phone.
  const [method, setMethod] = useState(isPartner ? "email" : "phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("ident");
  const [mockCode, setMockCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, refresh, setUser } = useAuth();
  const nav = useNavigate();

  const sendOTP = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      if (method === "email") {
        if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
          toast.error("Enter a valid email address");
          setLoading(false);
          return;
        }
        const { data } = await api.post("/auth/email/send", { email });
        setMockCode(data.mock_code);
      } else {
        if (!phone.match(/^\+?\d{10,15}$/)) {
          toast.error("Enter a valid phone with country code, e.g. +919876500000");
          setLoading(false);
          return;
        }
        const { data } = await api.post("/auth/otp/send", { phone });
        setMockCode(data.mock_code);
      }
      setStep("code");
      toast.success("Mock OTP: 123456");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const targetRole = isAdmin ? "admin" : isPartner ? "restaurant" : "booker";
      let userObj;
      if (method === "email") {
        const { data } = await api.post("/auth/email/verify", { email, code, role: targetRole });
        setToken(data.token);
        setUser(data.user);
        userObj = data.user;
      } else {
        userObj = await login(phone, code, targetRole);
      }
      toast.success("Welcome to Tablo!");
      if (userObj.role === "admin") nav("/admin");
      else if (userObj.role === "restaurant") nav("/partner/inbox");
      else if (!userObj.onboarded) nav("/onboarding");
      else nav("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (m) => {
    setMethod(m);
    setStep("ident");
    setCode("");
    setMockCode("");
  };

  return (
    <div className="min-h-screen bg-cream text-ink flex flex-col">
      <div className="max-w-md mx-auto w-full px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-light-ink hover:text-ink" data-testid="auth-back-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 -mt-10">
        <div className="w-full max-w-md tablo-card p-8 animate-fade-up" data-testid="auth-card">
          <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">
            {isAdmin ? "Admin sign-in" : isPartner ? "Restaurant sign-in" : "Sign in"}
          </p>
          <h1 className="font-serif text-3xl mb-2">Welcome to Tablo</h1>
          <p className="text-light-ink text-sm mb-6">
            {step === "ident"
              ? method === "email"
                ? "Enter your work email. We'll send a 6-digit code."
                : "Enter your mobile number. We'll send a 6-digit code."
              : "Enter the code we just sent."}
          </p>

          {/* Method switch — only useful for partners (booker stays on phone, admin stays on phone) */}
          {isPartner && step === "ident" && (
            <div className="flex bg-cream rounded-full p-1 mb-5 border border-tablo-border" data-testid="auth-method-switch">
              <button
                type="button"
                onClick={() => switchMethod("email")}
                className={`flex-1 text-xs uppercase tracking-wider rounded-full py-2 inline-flex items-center justify-center gap-1.5 transition-colors ${method === "email" ? "bg-ink text-cream" : "text-light-ink hover:text-ink"}`}
                data-testid="auth-method-email"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.6} /> Email
              </button>
              <button
                type="button"
                onClick={() => switchMethod("phone")}
                className={`flex-1 text-xs uppercase tracking-wider rounded-full py-2 inline-flex items-center justify-center gap-1.5 transition-colors ${method === "phone" ? "bg-ink text-cream" : "text-light-ink hover:text-ink"}`}
                data-testid="auth-method-phone"
              >
                <Smartphone className="w-3.5 h-3.5" strokeWidth={1.6} /> Phone
              </button>
            </div>
          )}

          {step === "ident" ? (
            <form onSubmit={sendOTP} className="space-y-4">
              {method === "email" ? (
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="partner@yourrestaurant.in"
                    className="bg-white border-tablo-border"
                    data-testid="auth-email-input"
                  />
                  <p className="text-[11px] text-light-ink/70 mt-1.5">Demo partner: <span className="font-medium text-ink">partner@olivebistro.in</span></p>
                </div>
              ) : (
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Phone</label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+919876500000"
                    className="bg-white border-tablo-border"
                    data-testid="auth-phone-input"
                  />
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full tablo-btn-primary" data-testid="auth-send-otp-btn">
                {loading ? "Sending..." : "Send code"}
              </Button>
              <p className="text-xs text-light-ink/70 text-center">
                Mock OTP is <span className="font-medium text-ink">123456</span>.
              </p>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">6-digit code</label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={mockCode || "123456"}
                  inputMode="numeric"
                  maxLength={6}
                  className="bg-white border-tablo-border tracking-[0.4em] text-center text-xl"
                  data-testid="auth-otp-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full tablo-btn-primary" data-testid="auth-verify-btn">
                {loading ? "Verifying..." : "Verify & Continue"}
              </Button>
              <button
                type="button"
                onClick={() => setStep("ident")}
                className="text-xs text-light-ink/70 w-full text-center"
                data-testid="auth-change-ident-btn"
              >
                Change {method === "email" ? "email" : "phone"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
